import React from 'react';
import { IGlobalStorefrontSettings, IStorefrontLink, IStorefrontSettings, IStorefrontTheme } from '../../../models/Storefront';
import './StorefrontEditor.css';
import StorefrontHeaderEditor from './Header/StorefrontHeaderEditor';
import StorefrontFooterEditor from './Footer/StorefrontFooterEditor';
import { CommandBar, ICommandBarItemProps, Icon, Panel, PanelType, Stack, TextField, Toggle } from '@fluentui/react';
import MarkdownView from 'react-showdown';
import { EditPencil, ElementPropertyRows, elementStyleFrom, StyleOverridesGroup, StylePartAccordions } from './styleControls';
import { stylePartForConfigKey, styleTargetForConfigKey } from './styleBulkApply';
import { STYLE_PART_ICONS } from './stylesConfig';
import { resolvedStyleValue } from './styleConfig';
import { lintStyleSections } from './headerFooterConfig';
import RawJsonEditor from './RawJsonEditor';
import { IBundlePageConfig, BUNDLE_PAGE_SECTIONS, parseBundlePageConfig, serializeBundlePageConfig } from './bundlePageConfig';
import { IStorefrontProductView, IStorefrontBundleView } from '../../../models/StorefrontProducts';
import PreviewCanvas from './PreviewCanvas';

// Canonical child-editor prop contract shared by every page editor (see StorefrontPagesEditorPage).
interface IStorefrontBundlePageEditorProps {
    globalSettings: IGlobalStorefrontSettings;
    globalTheme?: IStorefrontTheme;
    storefrontSettings: IStorefrontSettings;
    storefrontTheme?: IStorefrontTheme;
    editMode?: boolean;
    onStorefrontSettingsChange: (newSettings: IStorefrontSettings) => void;
    storefrontHeaderLinks: IStorefrontLink[];
    globalHeaderLinks: IStorefrontLink[];
    onHeaderLinksChange: (newLinks: IStorefrontLink[]) => void;
    storefrontFooterLinks: IStorefrontLink[];
    globalFooterLinks: IStorefrontLink[];
    onFooterLinksChange: (newLinks: IStorefrontLink[]) => void;
    sampleProducts?: IStorefrontProductView[];
    sampleBundles?: IStorefrontBundleView[];
}

// Only the style-section keys (not the boolean/number behavior flags) get style controls.
type SectionKey = typeof BUNDLE_PAGE_SECTIONS[number];

// The command bar's four topical panels (this replaced a nine-button toolbar laid out in three
// labeled columns). Each panel owns a group of the page's style sections as one-open-at-a-time
// accordions, and the one panel with a behavior setting attached (Description) carries it above
// them, so no page setting is left without a natural home and there is no far-side Options menu.
type PanelKey = 'page' | 'details' | 'description' | 'purchase';

const money = (n: number) => `$${n.toFixed(2)}`;

const StorefrontBundlePageEditor: React.FC<IStorefrontBundlePageEditorProps> = (props) => {
    const backgroundColor = resolvedStyleValue(props.storefrontSettings, 'backgroundColor') || props.storefrontTheme?.backgroundColor || props.globalSettings.backgroundColor || props.globalTheme?.backgroundColor || '#fff';
    const textColor = resolvedStyleValue(props.storefrontSettings, 'textColor') || props.storefrontTheme?.textColor || props.globalSettings.textColor || props.globalTheme?.textColor || '#242424';
    const defaultFont = resolvedStyleValue(props.storefrontSettings, 'defaultFont') || props.globalSettings.defaultFont || undefined;

    const config = React.useMemo(() => parseBundlePageConfig(props.storefrontSettings.bundlePageConfig), [props.storefrontSettings.bundlePageConfig]);

    // One panel open at a time; its command bar button is highlighted while it is. `part` names the
    // accordion to expand, so a click on a previewed element (or its pencil) lands on that
    // element's own settings rather than on the panel's first group.
    const [openPanel, setOpenPanel] = React.useState<{ key: PanelKey; part?: string } | null>(null);
    const closePanel = () => setOpenPanel(null);
    const openAt = (key: PanelKey, part?: string) => setOpenPanel({ key, part });

    const updateConfig = (patch: Partial<IBundlePageConfig>) =>
        props.onStorefrontSettingsChange({ ...props.storefrontSettings, bundlePageConfig: serializeBundlePageConfig({ ...config, ...patch }) });
    const updateSection = (key: SectionKey, patch: Partial<IBundlePageConfig[SectionKey]>) =>
        updateConfig({ [key]: { ...(config[key] ?? {}), ...patch } });

    // Resolved inline styles for the preview elements.
    // The part passed here is the one implied by the config key (stylePartForKey), so an element
    // carrying a $styleRef resolves against the right part of the referenced Storefront Style.
    const s = (key: SectionKey) => elementStyleFrom(config[key], stylePartForConfigKey('bundlePageConfig', key));

    // Click-to-edit wiring for a previewed element: the whole element opens its panel with its own
    // accordion expanded, and the hover pencil is the visible affordance for the same action.
    // EVERY editable element here sits inside the click-to-edit page container, so the click always
    // has to stop before it reaches an ancestor that would open a different panel. EditPencil stops
    // propagation on its own.
    const editable = (panel: PanelKey, part: SectionKey, what: string): React.HTMLAttributes<HTMLElement> => props.editMode
        ? {
            className: 'sf-pencil-host sf-editable',
            title: `Edit the ${what}`,
            onClick: (e) => { e.stopPropagation(); openAt(panel, part); },
        }
        : { className: 'sf-pencil-host' };
    const pencil = (panel: PanelKey, part: SectionKey, what: string) =>
        props.editMode ? <EditPencil onClick={() => openAt(panel, part)} title={`Edit ${what}`} /> : null;

    // The two accordion bodies. A part whose config key maps to a named Storefront Style
    // (addToCartButton / addToCartLabel, per styleBulkApply's allow-list) leads with the picker and
    // folds its raw rows under "Advanced overrides"; every other part is the bare flat row surface.
    const rowsPart = (key: SectionKey) => (
        <ElementPropertyRows settingKeyPrefix={`storefront.bundlePage.${key}`} value={config[key]} onChange={(patch) => updateSection(key, patch)} />
    );
    const styledPart = (key: SectionKey) => (
        <StyleOverridesGroup
            rowLayout
            target={styleTargetForConfigKey('bundlePageConfig', key)}
            value={config[key]}
            onChange={(patch) => updateSection(key, patch)}
        >
            <ElementPropertyRows settingKeyPrefix={`storefront.bundlePage.${key}`} value={config[key]} onChange={(patch) => updateSection(key, patch)} />
        </StyleOverridesGroup>
    );

    // First real bundle (if the parent supplied live storefront data); undefined => facsimile preview.
    const b = props.sampleBundles?.[0];

    // The Bundle Page Style bar. One button, one panel; `checked` renders the pressed highlight
    // while that entry's panel is open (mirrors the Home, Search and Product page editors' bars).
    // Every entry carries a `title` tooltip saying what it opens: the bar's short labels name the
    // topic, but (unlike the labeled-columns toolbar this replaced) they don't describe the elements.
    const styleBarItems: ICommandBarItemProps[] = [
        { key: 'page', text: 'Page', title: 'The box around the whole bundle page, and the bundle name heading.', iconProps: { iconName: 'GridViewMedium' }, checked: openPanel?.key === 'page', onClick: () => openAt('page') },
        { key: 'details', text: 'Details', title: 'The bundle image and the table of products included in the bundle.', iconProps: { iconName: 'ProductList' }, checked: openPanel?.key === 'details', onClick: () => openAt('details') },
        { key: 'description', text: 'Description', title: 'The description block: whether it collapses behind a "Show more" toggle, and how it looks.', iconProps: { iconName: 'AlignLeft' }, checked: openPanel?.key === 'description', onClick: () => openAt('description') },
        { key: 'purchase', text: 'Purchase', title: 'The price, the quantity box and the Add to Cart control.', iconProps: { iconName: 'ShoppingCart' }, checked: openPanel?.key === 'purchase', onClick: () => openAt('purchase') },
    ];

    return (
        <div>
            {props.editMode && (
                <div style={{ marginBottom: '1em' }}>
                <RawJsonEditor
                    label='Bundle Page'
                    liveObject={config}
                    onApply={(json) => props.onStorefrontSettingsChange({ ...props.storefrontSettings, bundlePageConfig: json })}
                    // The shared section linter checks every CSS leaf with CSS.supports(); the
                    // behavior boolean and the collapsed-height number are named so they validate
                    // as themselves rather than warning as unknown keys.
                    lint={(raw) => lintStyleSections(raw, BUNDLE_PAGE_SECTIONS, ['descriptionCollapsible'], [], ['descriptionCollapsedHeight'])}
                    keysHint={`Sections: ${BUNDLE_PAGE_SECTIONS.join(', ')}. Behavior keys: descriptionCollapsible (boolean), descriptionCollapsedHeight (number of px). Any CSS property (camelCase, e.g. boxShadow) works. Numbers become px (use a string for other units, e.g. "600" for font-weight) and fontSize is a percent. url(), @import, javascript: and expression() are blocked. Changes apply while the JSON is valid.`}
                >
                    <CommandBar
                        items={styleBarItems}
                        styles={{ root: { padding: 0, border: '1px solid #edebe9', borderRadius: 4, background: '#faf9f8' } }}
                    />
                </RawJsonEditor>
                {/* Per-topic style panels: right-side flyouts (like the home section editor).
                    Non-blocking so the preview stays live; the open panel's command bar button is
                    highlighted. Each is the same flat surface every other style panel uses: a
                    part accordion over flat property rows, one scroll, no tabs.
                    The React `key` re-seeds the open accordion when a SECOND element of the same
                    panel is clicked while that panel is already open: initialOpenKey is a seed, so
                    without the remount the accordion would stay on the first element. */}
                <Panel isOpen={openPanel?.key === 'page'} onDismiss={closePanel} headerText='Page' type={PanelType.smallFixedFar} isBlocking={false} closeButtonAriaLabel='Close'>
                    <StylePartAccordions key={openPanel?.part} initialOpenKey={openPanel?.part} parts={[
                        { key: 'container', label: 'Page Container', iconName: STYLE_PART_ICONS.band, tooltip: 'The box around the whole bundle page: its background, border and inner padding.', content: rowsPart('container') },
                        { key: 'title', label: 'Title', iconName: STYLE_PART_ICONS.heading, tooltip: 'The bundle name heading at the top of the page.', content: rowsPart('title') },
                    ]} />
                </Panel>
                {/* Details panel: what the bundle IS, i.e. its image and the products inside it */}
                <Panel isOpen={openPanel?.key === 'details'} onDismiss={closePanel} headerText='Details' type={PanelType.smallFixedFar} isBlocking={false} closeButtonAriaLabel='Close'>
                    <StylePartAccordions key={openPanel?.part} initialOpenKey={openPanel?.part} parts={[
                        { key: 'image', label: 'Image', iconName: STYLE_PART_ICONS.image, tooltip: 'The bundle image on the left of the top section.', content: rowsPart('image') },
                        { key: 'includedTable', label: 'Included Products', iconName: 'ProductList', tooltip: 'The table of products the bundle contains, between the purchase section and the description.', content: rowsPart('includedTable') },
                    ]} />
                </Panel>
                {/* Description panel: the collapsible behavior is this panel's primary content, so
                    the style group below it starts collapsed rather than burying it. */}
                <Panel isOpen={openPanel?.key === 'description'} onDismiss={closePanel} headerText='Description' type={PanelType.smallFixedFar} isBlocking={false} closeButtonAriaLabel='Close'>
                    <Stack tokens={{ childrenGap: 8 }} style={{ marginTop: 12 }}>
                        <Toggle
                            label='Collapsible'
                            inlineLabel
                            checked={!!config.descriptionCollapsible}
                            onChange={(_, checked) => updateConfig({ descriptionCollapsible: checked })}
                            styles={{ root: { marginBottom: 0 } }}
                        />
                        {config.descriptionCollapsible && (
                            <TextField
                                label='Collapsed height (px)'
                                type='number'
                                min={40}
                                value={config.descriptionCollapsedHeight?.toString() ?? ''}
                                placeholder='160'
                                onChange={(_, v) => { const n = v ? parseInt(v, 10) : undefined; updateConfig({ descriptionCollapsedHeight: (n != null && !isNaN(n)) ? n : undefined }); }}
                                styles={{ root: { width: 160 } }}
                            />
                        )}
                    </Stack>
                    <StylePartAccordions startCollapsed key={openPanel?.part} initialOpenKey={openPanel?.part} parts={[
                        { key: 'description', label: 'Description Style', iconName: STYLE_PART_ICONS.body, tooltip: 'The description block below the included-products table.', content: rowsPart('description') },
                    ]} />
                </Panel>
                {/* Purchase panel: everything in the buying column beside the image */}
                <Panel isOpen={openPanel?.key === 'purchase'} onDismiss={closePanel} headerText='Purchase' type={PanelType.smallFixedFar} isBlocking={false} closeButtonAriaLabel='Close'>
                    <StylePartAccordions key={openPanel?.part} initialOpenKey={openPanel?.part} parts={[
                        { key: 'price', label: 'Price', iconName: STYLE_PART_ICONS.price, tooltip: 'The bundle price heading.', content: rowsPart('price') },
                        { key: 'quantity', label: 'Quantity', iconName: STYLE_PART_ICONS.input, tooltip: 'The quantity input above the Add to Cart control.', content: rowsPart('quantity') },
                        // Both halves of the add-to-cart control are named-style targets: the round
                        // button is a button's base part, the text beside it is its label part.
                        { key: 'addToCartButton', label: 'Add to Cart Button', iconName: STYLE_PART_ICONS.base, tooltip: 'The round cart button inside the white add-to-cart card.', content: styledPart('addToCartButton') },
                        { key: 'addToCartLabel', label: 'Add to Cart Label', iconName: 'FontColorA', tooltip: 'The "Add to Cart" text beside that button.', content: styledPart('addToCartLabel') },
                    ]} />
                </Panel>
                </div>
            )}

            <PreviewCanvas style={{ backgroundColor }}>
                <StorefrontHeaderEditor
                    storefrontSettings={props.storefrontSettings}
                    storefrontTheme={props.storefrontTheme}
                    globalSettings={props.globalSettings}
                    globalTheme={props.globalTheme}
                    onStorefrontSettingsChange={props.onStorefrontSettingsChange}
                    editMode={false}
                    storefrontLinks={props.storefrontHeaderLinks}
                    globalLinks={props.globalHeaderLinks}
                    onLinksChange={props.onHeaderLinksChange}
                />

                <div style={{ color: textColor, fontFamily: defaultFont, padding: '1.5em' }}>
                    {/* Page container: width 100% / maxWidth 800 / centered — mirrors BundlePage's outer div */}
                    <div {...editable('page', 'container', 'page container')} style={{ width: '100%', maxWidth: 800, margin: '0 auto', position: 'relative', ...s('container') }}>
                        {pencil('page', 'container', 'page container')}

                        {/* Bundle name — <h1 lineHeight 1.3> */}
                        <h1 {...editable('page', 'title', 'bundle title')} style={{ position: 'relative', lineHeight: 1.3, marginTop: 0, ...s('title') }}>{pencil('page', 'title', 'title')}{b?.name ?? 'Office Network Starter Kit'}</h1>

                        {/* Top section mirrors BundlePage: .product-image floats left (300px); price / qty /
                            add-to-cart sit in the .product-data right column (width calc(100% - 350px)). */}
                        <div>
                            {/* Bundle image — .product-image: float left, width 300px, margin-right 1em */}
                            <div {...editable('details', 'image', 'bundle image')} style={{ position: 'relative', float: 'left', width: 300, height: 220, marginRight: '1em', marginBottom: '1em', background: '#f3f2f1', borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', ...s('image') }}>
                                {pencil('details', 'image', 'bundle image')}
                                {b?.image
                                    ? <img src={b.image} alt={b.name ?? ''} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                                    : <span style={{ fontSize: '5em' }}>📦</span>}
                            </div>

                            {/* .product-data right column */}
                            <div style={{ float: 'left', width: 'calc(100% - 350px)' }}>
                                {/* Price — <h2 class product-price>; the storefront CSS shrinks it to fit-content
                                    so a filled/bordered chip hugs the number instead of spanning the column. */}
                                <h2 {...editable('purchase', 'price', 'price')} style={{ position: 'relative', marginTop: 0, width: 'fit-content', maxWidth: '100%', ...s('price') }}>{pencil('purchase', 'price', 'price')}{money(b?.price ?? 499)}</h2>

                                {/* Quantity — Fluent Input: full width, "Quantity:" prefix, right-aligned value */}
                                <div {...editable('purchase', 'quantity', 'quantity box')} style={{ position: 'relative', display: 'flex', alignItems: 'center', width: '100%', boxSizing: 'border-box', border: '1px solid #d1d1d1', borderRadius: 4, padding: '5px 10px', marginTop: '1em', marginBottom: '1em', ...s('quantity') }}>
                                    {pencil('purchase', 'quantity', 'quantity box')}
                                    <span>Quantity:&nbsp;</span>
                                    <span style={{ flex: 1, textAlign: 'right' }}>1</span>
                                </div>

                                {/* Add to cart (real: round primary button + green "Add to Cart" label inside
                                    the white rounded .card-add-to-cart pill — the button and label style separately).
                                    Each takes its own click; the white pill around them isn't styleable. */}
                                <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: '#fff', borderRadius: 13, boxShadow: '0 2px 8px rgba(0,0,0,0.10)', padding: '6px 12px', maxWidth: 200 }}>
                                    <button {...editable('purchase', 'addToCartButton', 'Add to Cart button')} style={{ position: 'relative', width: 30, height: 30, borderRadius: '50%', border: 'none', background: '#0f6cbd', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: 14, boxShadow: '0 2px 8px rgba(0,0,0,0.12)', flex: '0 0 auto', ...s('addToCartButton') }}>{pencil('purchase', 'addToCartButton', 'Add to Cart button')}<Icon iconName='ShoppingCart' /></button>
                                    <span {...editable('purchase', 'addToCartLabel', 'Add to Cart label')} style={{ position: 'relative', color: '#00744d', fontWeight: 600, fontSize: 14, ...s('addToCartLabel') }}>{pencil('purchase', 'addToCartLabel', 'Add to Cart label')}Add to Cart</span>
                                </div>
                            </div>
                        </div>

                        <div style={{ clear: 'both' }} />

                        {/* Included products — component list between the top section and description;
                            <table class bundle-table> width 100% */}
                        <div style={{ marginTop: '1em' }}>
                            <table {...editable('details', 'includedTable', 'included products table')} style={{ position: 'relative', width: '100%', ...s('includedTable') }}>
                                {pencil('details', 'includedTable', 'included products table')}
                                <thead>
                                    <tr>
                                        <th style={{ width: 110 }} />
                                        <th style={{ textAlign: 'left' }}>Product</th>
                                        <th style={{ width: 100, textAlign: 'center' }}>Quantity</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {b?.products?.length
                                        ? b.products.map((item) => (
                                            <tr key={item.id}>
                                                <td>
                                                    <div style={{ width: 100, height: 100, background: '#f3f2f1', borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                                                        {item.image
                                                            ? <img src={item.image} alt={item.name ?? ''} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                                                            : null}
                                                    </div>
                                                </td>
                                                <td>{item.name}</td>
                                                <td style={{ textAlign: 'center' }}>{item.quantity}</td>
                                            </tr>
                                        ))
                                        : [
                                            { name: '8-Port Gigabit Switch', qty: 1, emoji: '📦' },
                                            { name: 'Wi-Fi 6 Access Point', qty: 1, emoji: '📶' },
                                            { name: 'CAT6 Patch Cable (10-Pack)', qty: 2, emoji: '🔌' },
                                        ].map((item) => (
                                            <tr key={item.name}>
                                                <td>
                                                    <div style={{ width: 100, height: 100, background: '#f3f2f1', borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '2.2em' }}>{item.emoji}</div>
                                                </td>
                                                <td>{item.name}</td>
                                                <td style={{ textAlign: 'center' }}>{item.qty}</td>
                                            </tr>
                                        ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Description — <p class product-description>. Real descriptions are a mix of
                            markdown and raw HTML; MarkdownView (showdown) handles both, exactly like the
                            shopper page (block output, so the host is a div rather than a p). */}
                        <div style={{ paddingTop: '1em' }}>
                            <div {...editable('description', 'description', 'description')} style={{ position: 'relative', ...s('description') }}>
                                {pencil('description', 'description', 'description')}
                                {b?.description
                                    ? <MarkdownView markdown={b.description} options={{ tables: true, emoji: true }} />
                                    : <p style={{ margin: 0 }}>Everything a small office needs to get connected — a managed switch, wireless access point, and cabling — bundled together at a discount.</p>}
                            </div>
                        </div>
                    </div>
                </div>

                <StorefrontFooterEditor
                    storefrontSettings={props.storefrontSettings}
                    storefrontTheme={props.storefrontTheme}
                    globalSettings={props.globalSettings}
                    globalTheme={props.globalTheme}
                    onStorefrontSettingsChange={props.onStorefrontSettingsChange}
                    editMode={false}
                    footerLinks={props.storefrontFooterLinks}
                    globalFooterLinks={props.globalFooterLinks}
                />
            </PreviewCanvas>
        </div>
    );
};

export default StorefrontBundlePageEditor;
